import { useCallback, useContext, useEffect } from 'react';
import { Box, Form, Heading } from '@zillow/constellation-10';
import type { FormApi } from 'final-form';
import { Form as ReactFinalForm } from 'react-final-form';
import KiqQuestion from './KiqQuestion';
import {
  useId,
  useIdVerificationCallbacks,
  useKiqQuestionSet,
  usePidAnswers,
  useSetPidRemainingAttempts,
  useTermSet,
} from '../../../IdvContext';
import { useIdVerificationWebApiHandler_submitIdentityCheckAnswers } from '@zg-rentals/ts-id-verification-client-react-query';
import PrivacyCard from '../../PrivacyCard';
import { getZgAuthZoneFromTermSet } from '../../../utils/zgAuthZoneUtils';
import { ExitReason, LogLevel, TermSet } from '../../../../../models';
import { LANDLORD_CLICKSTREAM_EVENTS, RENTER_CLICKSTREAM_EVENTS } from '../../../clickstream/events';
import { ErrorCodes } from '../../../constants/errorCodes';
import { DatarouterError } from '@zg-rentals/web-base';
import { ErrorWizardStepId } from '../../../wizard/wizardTypes';
import { WizardContext } from '../../../wizard/WizardContext';
import { LANDLORD_METRIC_PREFIX, RENTER_METRIC_PREFIX } from '../../../Idv';

// Mutation key for submitIdentityCheckAnswers
export const submitIdentityAnswersMutationKey = ['IdVerificationWebApiHandler_submitIdentityCheckAnswers'];

function KiqQuestionsFormBody({ form, invalid }: { form: FormApi; invalid: boolean }) {
  const { setOnNext, setIsNextDisabled } = useContext(WizardContext);
  const kiqQuestions = useKiqQuestionSet();

  useEffect(() => {
    setOnNext(() => () => {
      form.submit();
    });
    return () => setOnNext(null);
  }, [form.submit, setOnNext]);

  useEffect(() => {
    setIsNextDisabled(invalid);
    return () => setIsNextDisabled(false);
  }, [invalid, setIsNextDisabled]);

  return (
    <Form>
      <Box css={{ display: 'flex', flexDirection: { base: 'column', lg: 'row' } }}>
        <Box css={{ display: 'flex', flexDirection: 'column', flexBasis: { lg: '60%' } }}>
          <Heading level={2} textStyle="heading-sm">
            Identity verification questions
          </Heading>
          {kiqQuestions.map((kiqQuestion, index) => (
            <KiqQuestion question={kiqQuestion} index={index} key={kiqQuestion.questionText} />
          ))}
        </Box>
        <Box css={{ display: 'flex', flexBasis: { lg: '40%' }, marginTop: { xlDown: 'loose' } }}>
          <span>
            <PrivacyCard />
          </span>
        </Box>
      </Box>
    </Form>
  );
}

export default function KiqStep() {
  const requestingClient = useId();
  const pidAnswers = usePidAnswers();
  const termSet = useTermSet();
  const ssnProvided = Boolean(pidAnswers.ssn);
  const setPidRemainingAttempts = useSetPidRemainingAttempts();
  const { onExit, trackEvent, emitMetric, log } = useIdVerificationCallbacks();
  const { goToErrorStep } = useContext(WizardContext);

  const { mutate } = useIdVerificationWebApiHandler_submitIdentityCheckAnswers({
    mutationKey: submitIdentityAnswersMutationKey,
    onMutate: async () => {
      if (termSet === TermSet.LandlordScreening) {
        trackEvent(LANDLORD_CLICKSTREAM_EVENTS.clickNextSubmitKBA);
      } else {
        trackEvent(RENTER_CLICKSTREAM_EVENTS.submitKba);
      }
    },
    onSuccess: async () => {
      // Success means answers were correct and user is verified
      if (termSet === TermSet.LandlordScreening) {
        trackEvent(LANDLORD_CLICKSTREAM_EVENTS.idvSuccess);
        emitMetric(`${LANDLORD_METRIC_PREFIX} submitIdentityAnswers success`);
      } else {
        emitMetric(`${RENTER_METRIC_PREFIX} submitIdentityAnswers success`);
      }
      await onExit({ reason: ExitReason.IdVerified, success: true });
    },
    onError(error) {
      if (termSet === TermSet.LandlordScreening) {
        emitMetric(`${LANDLORD_METRIC_PREFIX} submitIdentityAnswers error`);
      } else {
        emitMetric(`${RENTER_METRIC_PREFIX} submitIdentityAnswers error`);
      }
      if (DatarouterError.is(error)) {
        switch (error.code) {
          case ErrorCodes.INCORRECT_ANSWERS: {
            const remainingAttempts =
              typeof error.errorDetails === 'object' &&
              error.errorDetails !== null &&
              'remainingAttempts' in error.errorDetails
                ? ((error.errorDetails as { remainingAttempts?: number }).remainingAttempts ?? null)
                : null;
            setPidRemainingAttempts(remainingAttempts);
            goToErrorStep(ErrorWizardStepId.IncorrectAnswers);
            break;
          }
          case ErrorCodes.USER_LOCKED_OUT:
            goToErrorStep(ErrorWizardStepId.UserLockedOut);
            break;
          default:
            log(LogLevel.Error, {
              message: `Unexpected error code for submitIdentityCheckAnswers: ${error.code}`,
              error,
            });
            goToErrorStep(ErrorWizardStepId.Generic);
            break;
        }
      } else {
        log(LogLevel.Error, {
          message: 'Error calling submitIdentityCheckAnswers',
          error,
        });
        goToErrorStep(ErrorWizardStepId.Generic);
      }
    },
  });

  const handleSubmit = useCallback(
    async (variables: Record<string, string>) => {
      const answers = Object.keys(variables)
        .sort()
        .map((key) => variables[key]);
      mutate({
        postData: {
          answers,
          deviceType: 'desktop',
          requestingClient,
          ssnProvided,
          zgAuthZone: getZgAuthZoneFromTermSet(termSet),
        },
      });
    },
    [mutate, requestingClient, ssnProvided, termSet],
  );

  return (
    <ReactFinalForm
      onSubmit={handleSubmit}
      render={({ form, invalid }) => <KiqQuestionsFormBody form={form} invalid={invalid} />}
    />
  );
}
